/*
 * Positionen.tsx - Auftrags- oder Reservierungspositionen darstellen.
 *
 * Created by Dr. Maximillian Dornseif 2021-12-06 in huWaWi3 24.7.2
 * Copyright (c) 2021, 2022 HUDORA GmbH
 */

import { LadeChecker, VerfuegbarRender, useSchema } from '@hudora/hd-react-components'
import { ValueRender } from '@hudora/react-jsonschema-valuerender'
import AutoFixHighOutlined from '@mui/icons-material/AutoFixHighOutlined'
import EditOutlined from '@mui/icons-material/EditOutlined'
import { IconButton, Typography } from '@mui/material'
import Chip from '@mui/material/Chip'
import Paper from '@mui/material/Paper'
import Table from '@mui/material/Table'
import TableBody from '@mui/material/TableBody'
import TableCell from '@mui/material/TableCell'
import TableContainer from '@mui/material/TableContainer'
import TableHead from '@mui/material/TableHead'
import TableRow from '@mui/material/TableRow'
import { JSONSchema7 } from 'json-schema'
import * as R from 'ramda'
import React from 'react'
import { gql, useQuery } from 'urql'

import { AUFTRAGSPOSITION_FRAGMENT, AUFTRAG_FRAGMENT } from '../../fragments'
import { HdEditIcon, HdStornoIcon } from '../../lib/components/HdIcons'
import { useRohdatenPanel3 } from '../../lib/hooks/useRohdatenPanel3'
import { LinkRender } from '../../lib/hd-link'
import { useEditDialog } from '../components/useEditDialog'
import { AUFTRAGPOS_QUERY } from './WaWiAuftragEinerQuery'

// Declaring type of props - see "Typing Component Props" for more examples
type DisplayPositionenProps = {
  auftrag: Record<string, any> // Auftrag oder Reservierung
  nr: string
} /* use `interface` if exporting so that consumers can extend */

export function AuftragsPositionen({ auftrag, nr }: DisplayPositionenProps) {
  const { schema } = useSchema('HwAuftragsPosition')
  const [{ data, fetching, error }] = useQuery({
    query: AUFTRAGPOS_QUERY,
    variables: { nr },
  })
  const [, rohdatenPanel, , renderRohdatenPanelButton] = useRohdatenPanel3({
    schemaName: 'HwAuftragsPosition',
    entity: undefined,
  })
  const [editPositionspreis, dialogPositionspreis] = useEditDialog({
    subSchema: schema?.properties?.preis as JSONSchema7,
    propertyName: 'preis',
    variables: {
      auftragKeyString: auftrag._keyStr,
      auftragRev: auftrag._rev,
      fieldName: 'preis',
      message: 'Preis aktualisiert',
    },
    mutation: AUFTRAGSPOSITION_FELD_MUTATION,
  })
  const [editMenge, dialogMenge] = useEditDialog({
    subSchema: schema?.properties?.menge_offen as JSONSchema7,
    propertyName: 'menge_offen',
    variables: {
      auftragKeyString: auftrag._keyStr,
      auftragRev: auftrag._rev,
      fieldName: 'menge_offen',
      message: 'Menge aktualisiert',
    },
    mutation: AUFTRAGSPOSITION_FELD_MUTATION,
  })
  // Zuteilungen haben wir wohl nur bei Aufträgen
  const zuteilungmap: Record<string, any> = R.fromPairs(
    (data?.entity?.zuteilungen || []).map((x) => [x.posnr_auftrag, x])
  )

  return (
    <LadeChecker loading={fetching} error={error} label={`Lade Positionen`}>
      {dialogMenge}
      {dialogPositionspreis}
      {rohdatenPanel}
      <section>
        <Typography color="text.secondary" gutterBottom variant="h5" component="h3">
          Positionen
          {/* <button class="btn btn-primary btn-xs"
           data-toggle="modal" data-target="#position-zufuegen-modal"
           >Zufügen</button> */}
        </Typography>
        <TableContainer component={Paper}>
          <Table sx={{ minWidth: 650 }} size="small" aria-label="a dense table">
            <TableHead>
              <TableRow>
                <TableCell></TableCell>
                <TableCell title="bestellt / geliefert / offen" align="right">
                  b&thinsp;/&thinsp;g&thinsp;/&thinsp;o
                </TableCell>
                <TableCell>№</TableCell>
                <TableCell>Name</TableCell>
                <TableCell align="right" style={{ whiteSpace: 'nowrap' }}>
                  Stück €
                </TableCell>
                <TableCell align="right" style={{ whiteSpace: 'nowrap' }}>
                  Wert €
                </TableCell>
                <TableCell></TableCell>
                <TableCell></TableCell>
                <TableCell>Resevierungen</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {data?.entity?.positionen?.map((pos) => (
                <TableRow key={pos.posnr}>
                  <TableCell>
                    {pos.erledigt ? (
                      <Chip label="erledigt" size="small" color="success" variant="outlined" />
                    ) : null}

                    {zuteilungmap[pos.posnr] ? (
                      <span title={zuteilungmap[pos.posnr]?.artnr}>
                        <LinkRender value={zuteilungmap[pos.posnr]?.komminr} />
                      </span>
                    ) : !pos.erledigt ? (
                      <VerfuegbarRender
                        itemNr={pos.bartnr}
                        kundenNr={auftrag?.kunde?.designator}
                        auftragsNr={auftrag?.designator}
                      />
                    ) : null}
                    {/* "storniert":false */}
                  </TableCell>
                  <TableCell style={{ whiteSpace: 'nowrap' }} align="right">
                    {/* {pos.menge_offen ? <AutoFixHighOutlined /> : null} */}
                    {pos.menge_offen > 0 ? (
                      <IconButton
                        size="small"
                        color="primary"
                        title="Bearbeiten"
                        onClick={(e) =>
                          editMenge(
                            pos.menge_offen,
                            {
                              posKeyString: pos._keyStr,
                              positionRev: pos._rev,
                            },
                            pos.bartnr
                          )
                        }
                      >
                        {' '}
                        <HdEditIcon />
                      </IconButton>
                    ) : null}
                    {pos.menge_bestellt} / {pos.menge_geliefert} / <b>{pos.menge_offen}</b>
                  </TableCell>

                  <TableCell>
                    <LinkRender value={pos.bartnr} />
                  </TableCell>
                  <TableCell>
                    {pos?.item?.name}
                    {pos.infotext_kunde ? (
                      <Typography color="text.secondary" fontSize="small">
                        {pos.infotext_kunde}
                      </Typography>
                    ) : null}
                  </TableCell>
                  <TableCell
                    title={`Preisherkunft: ${pos.preisherkunft}`}
                    style={{ whiteSpace: 'nowrap' }}
                    align="right"
                  >
                    {pos.menge_offen && pos.menge_geliefert === 0 ? (
                      <IconButton
                        size="small"
                        color="primary"
                        title="Bearbeiten"
                        onClick={(e) =>
                          editPositionspreis(
                            pos.preis,
                            {
                              posKeyString: pos._keyStr,
                              positionRev: pos._rev,
                            },
                            pos.bartnr
                          )
                        }
                      >
                        <HdEditIcon />
                      </IconButton>
                    ) : null}
                    <ValueRender value={pos.preis} format="eurocent" />
                    {pos.item?.preisResolved?.fh && pos.preis !== pos.item?.preisResolved?.fh ? (
                      <Typography color="text.secondary" fontSize="small">
                        {'Liste '}
                        <ValueRender value={pos?.item?.preisResolved?.fh} format="eurocent" />{' '}
                      </Typography>
                    ) : null}
                  </TableCell>
                  <TableCell align="right" style={{ whiteSpace: 'nowrap' }}>
                    <ValueRender value={pos.preis * pos.menge_bestellt} format="eurocent" />
                  </TableCell>
                  <TableCell></TableCell>
                  <TableCell>
                    {/* {pos.menge_offen ? <HdStornoIcon /> : null} */}
                    {renderRohdatenPanelButton({ pos, zuteilung: zuteilungmap[pos.posnr] })}
                  </TableCell>
                  <TableCell>
                    {pos.reservierungsabrufe?.map((abruf) => (
                      <>
                        <LinkRender value={abruf.reservierungsnr} />{' '}
                      </>
                    ))}
                  </TableCell>
                </TableRow>
              ))}
              {/* {% if pos.infotext_kunde %}
             <tr><th>Kundenreferenz:</th><td colspan="4">{{ pos.infotext_kunde|e }}</td></tr>
            {% endif %} */}
              {/* {% else %}
             <tr><td><strong>keine Positionen vorhanden</strong></td></tr> */}
            </TableBody>
          </Table>
        </TableContainer>
      </section>
    </LadeChecker>
  )
}

const AUFTRAGSPOSITION_FELD_MUTATION = gql`
  mutation AuftragsPositionPreisMutation(
    $auftragKeyString: ID!
    $auftragRev: String!
    $posKeyString: ID!
    $positionRev: String!
    $fieldName: String!
    $value: Int!
    $message: String
  ) {
    result: updateHwAuftragsPositionNumber(
      auftragKeyString: $auftragKeyString
      auftragRev: $auftragRev
      posKeyString: $posKeyString
      positionRev: $positionRev
      fieldName: $fieldName
      value: $value
      message: $message
    ) {
      position {
        ...auftragspositionFragment
      }
      auftrag {
        ...auftragFragment
      }
      success
      message
      oldRev
      newRev
      duration
    }
  }
  ${AUFTRAGSPOSITION_FRAGMENT}
  ${AUFTRAG_FRAGMENT}
`
